export type WorkflowScheduleRecurrenceKind = 'Cron' | 'Interval'

export type WorkflowScheduleState = 'Active' | 'Paused'

export type WorkflowSchedulePausedReason = 'User' | 'AutoPausedAfterFailures' | 'TargetDeleted' | 'RecurrenceInvalid'

export type WorkflowScheduleOccurrenceStatus = 'Due' | 'Missed' | 'Triggered' | 'Started' | 'Skipped' | 'Failed'

export type WorkflowScheduleOccurrenceReason =
  | 'OnTime'
  | 'MissedDuringDowntime'
  | 'SchedulePaused'
  | 'ScheduleDeleted'
  | 'ScheduleChanged'
  | 'TargetDeleted'
  | 'TargetInactive'
  | 'TargetShadowed'
  | 'OwnerLacksAccess'
  | 'ValidationFailed'
  | 'DispatchFailed'
  | 'SchedulerDisabled'

export const WORKFLOW_SCHEDULE_OCCURRENCE_FILTER_STATUSES: WorkflowScheduleOccurrenceStatus[] = [
  'Triggered', 'Started', 'Skipped', 'Failed',
]

export const WORKFLOW_SCHEDULE_PAUSED_REASON_LABELS: Record<WorkflowSchedulePausedReason, string> = {
  User: 'Paused by a user',
  AutoPausedAfterFailures: 'Paused automatically after 3 consecutive failed fires',
  TargetDeleted: 'Paused automatically because its workflow was deleted',
  RecurrenceInvalid: 'Paused automatically: the recurrence can no longer be calculated (for example, its time zone is no longer known). Edit the schedule and resume it.',
}

export const WORKFLOW_SCHEDULE_OCCURRENCE_REASON_LABELS: Record<WorkflowScheduleOccurrenceReason, string> = {
  OnTime: 'On time',
  MissedDuringDowntime: 'Missed during downtime',
  SchedulePaused: 'Schedule was paused',
  ScheduleDeleted: 'Schedule was deleted',
  ScheduleChanged: 'Schedule was changed',
  TargetDeleted: 'Workflow was deleted',
  TargetInactive: 'Workflow is inactive',
  TargetShadowed: 'Workflow name now resolves to a different definition',
  OwnerLacksAccess: 'Owner can no longer trigger this workflow',
  ValidationFailed: 'Trigger validation failed',
  DispatchFailed: 'Dispatch failed',
  SchedulerDisabled: 'Scheduler turned off',
}

export interface WorkflowScheduleSummary {
  id: string
  projectId: string
  name: string
  targetDefinitionId: string
  targetName: string | null
  targetIsActive: boolean
  recurrenceKind: WorkflowScheduleRecurrenceKind
  cronExpression: string | null
  intervalMinutes: number | null
  timeZoneId: string
  ownerUserId: string
  ownerDisplayName: string | null
  state: WorkflowScheduleState
  pausedReason: WorkflowSchedulePausedReason | null
  nextOccurrenceUtc: string | null
  lastStatus: WorkflowScheduleOccurrenceStatus | null
  lastScheduledForUtc: string | null
  skippedCount: number
  failedCount: number
}

export interface WorkflowSchedule extends WorkflowScheduleSummary {
  description: string | null
  anchorUtc: string
  inputValuesJson: string
  pausedAtUtc: string | null
  pausedByUserId: string | null
  pausedByDisplayName: string | null
  consecutiveFailureCount: number
  lastProcessedOccurrenceUtc: string
  creationTime: string
  creatorUserId: string | null
  lastModificationTime: string | null
  lastModifierUserId: string | null
}

export interface WorkflowScheduleOccurrence {
  id: string
  scheduleId: string
  scheduledForUtc: string
  status: WorkflowScheduleOccurrenceStatus
  reason: WorkflowScheduleOccurrenceReason
  reasonDetail: string | null
  detectedAtUtc: string
  triggeredAtUtc: string | null
  resolvedAtUtc: string | null
  workflowRunId: string | null
}

export interface ScheduleOccurrencePreview {
  utc: string
  local: string
  dstNote: string | null
}

export interface WorkflowSchedulePreviewResponse {
  occurrences: ScheduleOccurrencePreview[]
}

export interface WorkflowScheduleOrigin {
  scheduleId: string
  scheduleName: string | null
  scheduledForUtc: string
  isCatchUp: boolean
}

export interface WorkflowScheduleCreated {
  id: string
}

export interface WorkflowScheduleRecurrenceRequest {
  recurrenceKind: WorkflowScheduleRecurrenceKind
  cronExpression?: string | null
  intervalMinutes?: number | null
  anchorUtc?: string | null
  timeZoneId: string
}

export interface CreateWorkflowScheduleRequest extends WorkflowScheduleRecurrenceRequest {
  name: string
  description?: string | null
  targetDefinitionId: string
  inputValuesJson: string
  active: boolean
}

export interface UpdateWorkflowScheduleRequest extends WorkflowScheduleRecurrenceRequest {
  name: string
  description?: string | null
  inputValuesJson: string
}


export interface WorkflowScheduleListFilters {
  state?: WorkflowScheduleState
}

export interface WorkflowScheduleOccurrenceFilters {
  status?: WorkflowScheduleOccurrenceStatus
  fromUtc?: string
  toUtc?: string
}
